import { IExercisePresetWorkoutSet } from '#types/exercisePresets';
import { match, P } from 'ts-pattern';
import { BeforeAfterPhoto } from './RoundInfo/BeforeAfterPhoto';
import {
  IDashBoardChartForm,
  IExerciseRecord,
  Inbody,
  PainPart
} from './types';

// Validation for IExercisePresetWorkoutSet
function isValidSet(set: IExercisePresetWorkoutSet): boolean {
  return match(set)
    .with({ setType: 'REP', reps: P.number }, ({ reps }) => {
      return reps > 0;
    })
    .with(
      { setType: 'REPWEIGHT', reps: P.number, weight: P.number },
      ({ reps, weight }) => reps > 0 && weight > 0
    )
    .with(
      { setType: 'REPTIME', reps: P.number, time: P.number },
      ({ reps, time }) => reps > 0 && time > 0
    )
    .with({ setType: 'TIME', time: P.number }, ({ time }) => time > 0)
    .otherwise(() => false);
}

// Validation for IExerciseRecord
function isValidExerciseRecord(record: any): record is IExerciseRecord {
  if (typeof record.exerciseKind !== 'string') {
    return false;
  }

  if (typeof record.exerciseTypeId !== 'string') {
    return false;
  }

  if (!Array.isArray(record.sets)) {
    return false;
  }

  for (const set of record.sets) {
    if (!isValidSet(set)) {
      return false;
    }
  }

  if (
    typeof record.exerciseComment !== 'undefined' &&
    typeof record.exerciseComment !== 'string'
  ) {
    return false;
  }

  if (typeof record.images !== 'undefined' && !Array.isArray(record.images)) {
    return false;
  }

  if (typeof record.videos !== 'undefined' && !Array.isArray(record.videos)) {
    return false;
  }

  return true;
}

// Validation for Inbody
function isValidInbody(inbody: Inbody | null): boolean {
  if (inbody === null) {
    return true;
  }

  const { skeletalMuscleMass, bodyFatMass, weight } = inbody;

  if (!weight && !skeletalMuscleMass && !bodyFatMass) {
    return true;
  }

  return match(inbody)
    .with(
      {
        weight: P.when(w => typeof w === 'number' && w > 0),
        bodyFatMass: P.when(bfm => typeof bfm === 'number' && bfm > 0),
        skeletalMuscleMass: P.when(smm => typeof smm === 'number' && smm > 0)
      },
      () => true
    )
    .otherwise(() => false);
}

// Validation for PainPart
function isValidPainPart(painPart: any): painPart is PainPart {
  return (
    typeof painPart.name === 'string' &&
    typeof painPart.level === 'number' &&
    painPart.level >= 1 &&
    painPart.level <= 10
  );
}

// Validation for IDashBoardChartForm
export function isValidDashboardChartForm(
  form: any
): form is IDashBoardChartForm {
  const isRecordsValid =
    Array.isArray(form.records) && form.records.every(isValidExerciseRecord);

  const isCommentValid =
    typeof form.comment === 'string' && form.comment !== '';

  const isInbodyValid =
    form.inbody === null ? true : isValidInbody(form.inbody);

  const isPainPartsValid = Array.isArray(form.painParts)
    ? form.painParts.every(isValidPainPart)
    : true;

  const isBeforeAfterPhotoIdValid =
    form.beforeAfterPhotoId === null || BeforeAfterPhoto.length === 0
      ? true
      : typeof form.beforeAfterPhotoId === 'string';

  return (
    isRecordsValid &&
    isCommentValid &&
    isInbodyValid &&
    isPainPartsValid &&
    isBeforeAfterPhotoIdValid
  );
}
